<template>
  <span class="ai-feedback-thumbs">
    <button type="button" class="thumb thumb-up" :class="{ 'active': rating === 'up' }" v-tooltip="'Correct'"
      @click.stop="handleUpClick" @mousedown="handleThumbMousedown">
      <vue-feather type="thumbs-up" :size="size" />
    </button>
    <button ref="downBtn" type="button" class="thumb thumb-down" :class="{ 'active': rating === 'down' }"
      v-tooltip="'Incorrect'" @click.stop="handleDownClick" @mousedown="handleThumbMousedown">
      <vue-feather type="thumbs-down" :size="size" />
    </button>

    <!-- Optional reason popup — opens right after a thumbs-down; the down is already
         submitted, so abandoning this just leaves the bare down in place -->
    <FloatingDropdown ref="noteDropdown" :open="noteOpen" :trigger-ref="noteTrigger" :match-width="false"
      align="right" @dismiss="handleNoteDismiss">
      <div class="ai-thumbs-note" @click.stop>
        <input ref="noteInput" v-model="noteDraft" type="text" class="ai-thumbs-note-input"
          placeholder="What did the AI get wrong? (optional)" maxlength="2000"
          autocomplete="nope" data-lpignore="true" data-form-type="other"
          @keydown="handleNoteKeydown" @blur="handleNoteBlur" />
      </div>
    </FloatingDropdown>
  </span>
</template>

<script>
import VueFeather from 'vue-feather';
// Imported directly (not via @/design) because this component is itself part of the
// design barrel — a barrel import here creates a circular dependency that breaks
// webpack module initialization at app load.
import FloatingDropdown from '../floating/FloatingDropdown.vue';

// Only one reason popup open at a time across all thumbs on screen (banner, per-field
// rows, tag selector). Thumb buttons stop click propagation, so one instance's window
// click listener can't see clicks on a sibling's buttons — this registry closes the
// open one directly.
let openInstance = null;

export default {
  name: 'AiFeedbackThumbs',
  components: {
    'vue-feather': VueFeather,
    FloatingDropdown
  },
  props: {
    rating: { type: String, default: null }, // 'up' | 'down' | null
    size: { type: [Number, String], default: 14 }
  },
  emits: ['feedback'],
  data() {
    return { noteOpen: false, noteDraft: '', skipBlur: false, noteTrigger: null };
  },
  mounted() {
    window.addEventListener('click', this.handleOutsideClick);
  },
  unmounted() {
    window.removeEventListener('click', this.handleOutsideClick);
    if (openInstance === this) openInstance = null;
  },
  methods: {
    handleUpClick() {
      this.closeNote();
      const rating = this.rating === 'up' ? null : 'up';
      this.$emit('feedback', { rating });
    },
    handleDownClick() {
      if (this.rating === 'down') {
        // Uncheck — no reason popup
        this.closeNote();
        this.$emit('feedback', { rating: null });
        return;
      }
      // The bare down is submitted immediately; the reason is an optional follow-up
      this.$emit('feedback', { rating: 'down' });
      this.openNote();
    },
    // While the popup is open, don't let thumb mousedowns steal focus from the input —
    // otherwise blur-save fires before the click handler and double-submits.
    handleThumbMousedown(event) {
      if (this.noteOpen) event.preventDefault();
    },
    claimOpen() {
      if (openInstance && openInstance !== this) openInstance.closeNote();
      openInstance = this;
    },
    openNote() {
      this.noteTrigger = this.$refs.downBtn;
      this.noteDraft = '';
      this.skipBlur = false;
      this.claimOpen();
      this.noteOpen = true;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = this.$refs.noteInput;
          if (el) el.focus();
        });
      });
    },
    closeNote() {
      this.noteOpen = false;
      if (openInstance === this) openInstance = null;
    },
    // Saves only when there is text; an empty draft just closes (the bare down stays)
    saveNote() {
      const comment = this.noteDraft.trim();
      if (comment) {
        this.$emit('feedback', { rating: 'down', comment });
      }
      this.closeNote();
    },
    handleNoteBlur() {
      if (!this.noteOpen) return;
      if (this.skipBlur) {
        this.skipBlur = false;
        return;
      }
      this.saveNote();
    },
    // FloatingDropdown dismisses on scroll/resize — same rule as blur so scrolling
    // doesn't silently drop typed text
    handleNoteDismiss() {
      this.saveNote();
    },
    handleNoteKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.saveNote();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.skipBlur = true; // Esc cancels — don't let the blur auto-save
        this.closeNote();
      }
    },
    handleOutsideClick(event) {
      if (!this.noteOpen) return;
      if (this.$el.contains(event.target)) return;
      if (this.$refs.noteDropdown && this.$refs.noteDropdown.containsElement(event.target)) return;
      // The input had focus, so its blur already handled any save
      this.closeNote();
    }
  }
}
</script>

<style scoped>
.ai-feedback-thumbs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #9ca3af;
  padding: 3px;
  border-radius: 4px;
  cursor: pointer;
  line-height: 0;
}

.thumb:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #4b5563;
}

.thumb.active svg {
  fill: currentColor;
  fill-opacity: 0.15;
}

.thumb-up.active {
  color: #16a34a;
  background: #f0fdf4;
}

.thumb-down.active {
  color: #dc2626;
  background: #fef2f2;
}
</style>

<style>
/* Teleported popup — not scoped so styles apply when FloatingDropdown moves it to <body>. */
.ai-thumbs-note {
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  min-width: 310px;
  padding: 8px;
}

.ai-thumbs-note .ai-thumbs-note-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
  color: #374151;
  outline: none;
}

.ai-thumbs-note .ai-thumbs-note-input:focus {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}
</style>
